/**
 * API Service for Building Administration 3D Spatial Violation Management System
 * Handles all HTTP requests to the Django REST Framework backend
 */

import {
  type User,
  type CameraBookmark,
  type VectorDataset,
  type VectorLayer,
  type GeoJSONFeatureCollection,
  type ApiResponse,
  type PaginatedResponse,
  type ConversionStatusPointClouds,
  type DigitalElevationModelApiResponse,
  type TilesetApiResponse,
  type TMSServiceApiResponse,
  type PointCloudApiResponse,
  type ViolationLayer,
  type PointCloudCOPCApiResponse,
  type ElevationMode,
  type ViolationDetectionConfig,
  type NumericRange,
  type Role,
} from '@/types';
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import useAppStore from '@/store';
import type { RoleId, RolesResponse, RolePermission } from '@/types/roles';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL;
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 30000; // 30 seconds
const PROGRESS_TIMEOUT =
  Number(import.meta.env.VITE_PROGRESS_TIMEOUT) || 180000;
const REFRESH_ENDPOINT = '/auth/token/refresh/';

// Validate API URL is set
if (!API_BASE_URL) {
  console.error('VITE_API_URL is not configured. Please check your .env file.');
}

// Application Configuration
const APP_NAME = import.meta.env.VITE_APP_NAME || 'Building Administration 3D';
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';
const DEBUG_MODE = import.meta.env.VITE_DEBUG === 'true';

// Simple fetch wrapper with authentication
class ApiClient {
  private axiosInstance: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (error: unknown) => void;
    config: InternalAxiosRequestConfig;
  }> = [];

  constructor(baseURL: string, timeout: number = API_TIMEOUT) {
    this.axiosInstance = axios.create({
      baseURL,
      timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private redirectToLogin() {
    const { logout } = useAppStore.getState();
    logout();

    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `/login?returnUrl=${encodeURIComponent(currentPath)}&sessionExpired=true`;
  }

  private setupInterceptors() {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      async (error) => {
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes(REFRESH_ENDPOINT) &&
          localStorage.getItem('accessToken')
        ) {
          const refreshToken = localStorage.getItem('refreshToken');

          if (!refreshToken) {
            this.redirectToLogin();
            return Promise.reject(error);
          }

          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({
                resolve,
                reject,
                config: originalRequest,
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const response = await authApi.refreshToken(refreshToken);
            const { access, refresh } = response;

            localStorage.setItem('accessToken', access);
            localStorage.setItem('refreshToken', refresh);

            originalRequest.headers.Authorization = `Bearer ${access}`;

            this.failedQueue.forEach(({ resolve, config }) => {
              config.headers.Authorization = `Bearer ${access}`;
              resolve(this.axiosInstance(config));
            });
            this.failedQueue = [];

            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            this.failedQueue.forEach(({ reject }) => reject(refreshError));
            this.failedQueue = [];
            this.redirectToLogin();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private handleAxiosError(error: unknown): Error {
    if (!(error instanceof AxiosError)) {
      return error instanceof Error ? error : new Error(String(error));
    }

    if (error.code === 'ECONNABORTED') {
      return new Error('Request timeout');
    }

    if (!error.response) {
      return error.request
        ? new Error('Network error: No response received')
        : new Error(`Request setup error: ${error.message}`);
    }

    const { data, status } = error.response;

    if (!data || typeof data !== 'object') {
      return new Error(`HTTP error! status: ${status}`);
    }

    const errorData = data as any;

    if (errorData.detail) return new Error(errorData.detail);
    if (errorData.errors?.detail) return new Error(errorData.errors.detail);
    if (errorData.message) return new Error(errorData.message);
    if (errorData.error) return new Error(errorData.error);

    const fieldErrors = Object.entries(errorData)
      .map(([field, messages]) =>
        Array.isArray(messages)
          ? `${field}: ${messages.join(', ')}`
          : typeof messages === 'string'
            ? `${field}: ${messages}`
            : null
      )
      .filter(Boolean);

    return fieldErrors.length > 0
      ? new Error(`Validation errors: ${fieldErrors.join('; ')}`)
      : new Error(`HTTP error! status: ${status}`);
  }

  async get<T>(
    endpoint: string,
    params?: Record<string, unknown>,
    options?: Omit<AxiosRequestConfig, 'url' | 'method'>
  ): Promise<T> {
    try {
      const response = await this.axiosInstance.get<T>(endpoint, {
        params,
        ...options,
      });
      return response.data;
    } catch (error) {
      throw this.handleAxiosError(error);
    }
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    try {
      const response = await this.axiosInstance.post<T>(endpoint, data);
      return response.data;
    } catch (error) {
      throw this.handleAxiosError(error);
    }
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    try {
      const response = await this.axiosInstance.patch<T>(endpoint, data);
      return response.data;
    } catch (error) {
      throw this.handleAxiosError(error);
    }
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    try {
      const response = await this.axiosInstance.put<T>(endpoint, data);
      return response.data;
    } catch (error) {
      throw this.handleAxiosError(error);
    }
  }

  async delete<T = void>(endpoint: string): Promise<T> {
    try {
      const response = await this.axiosInstance.delete(endpoint);
      return (response.data ?? undefined) as T;
    } catch (error) {
      throw this.handleAxiosError(error);
    }
  }

  async postFormData<T>(
    endpoint: string,
    formData: FormData,
    onProgress?: (progress: number) => void,
    externalController?: AbortController
  ): Promise<T> {
    let lastProgress = 0;
    let abortReason = '';
    const controller = externalController || new AbortController();

    const resetTimeout = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        abortReason = `Upload stalled at ${lastProgress}% (no progress for 3 minutes)`;
        controller.abort();
      }, PROGRESS_TIMEOUT);
    };

    const timeoutRef = { current: null as NodeJS.Timeout | null };

    resetTimeout();

    try {
      const response = await this.axiosInstance.post<T>(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 0,
        signal: controller.signal,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentComplete = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );

            if (percentComplete > lastProgress) {
              lastProgress = percentComplete;

              if (percentComplete >= 100) {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
              } else {
                resetTimeout();
              }

              if (onProgress) {
                onProgress(percentComplete);
              }
            }
          }
        },
      });

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      return response.data;
    } catch (error) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      const axiosError = error as AxiosError;

      if (axiosError.code === 'ERR_CANCELED') {
        throw new Error(abortReason || 'Upload cancelled');
      }

      if (axiosError.code === 'ECONNABORTED') {
        throw new Error(
          `Upload stalled at ${lastProgress}% (no progress for 3 minutes)`
        );
      }

      throw this.handleAxiosError(axiosError);
    }
  }
}

// Create API client instance
const apiClient = new ApiClient(API_BASE_URL);

// Authentication API
export const authApi = {
  login: async (
    email: string,
    password: string
  ): Promise<ApiResponse<{ access: string; refresh: string; user: User }>> => {
    return apiClient.post('/auth/login/', { email, password });
  },

  register: async (userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role: string;
  }): Promise<ApiResponse<User>> => {
    return apiClient.post('/auth/register/', userData);
  },

  logout: async (): Promise<void> => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await apiClient.post('/auth/logout/', { refresh: refreshToken });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('rememberMe');
    }
  },

  refreshToken: async (
    refresh: string
  ): Promise<{ access: string; refresh: string }> => {
    return apiClient.post(REFRESH_ENDPOINT, { refresh });
  },

  getCurrentUser: async (): Promise<ApiResponse<User>> => {
    return apiClient.get('/auth/user/');
  },
};

// Tileset API
export const tilesetApi = {
  getTilesets: async (params?: {
    status?: string;
  }): Promise<PaginatedResponse<TilesetApiResponse>> => {
    return apiClient.get('/spatial-data/tilesets/', params);
  },

  createTileset: async (tilesetData: {
    name: string;
    description?: string;
    version?: string;
    status?: string;
    tileset_json_url: string;
    max_screen_space_error?: number;
    maximum_memory_usage?: number;
  }): Promise<ApiResponse<any>> => {
    return apiClient.post('/spatial-data/tilesets/', tilesetData);
  },

  deleteTileset: async (id: string): Promise<void> => {
    return apiClient.delete(`/spatial-data/tilesets/${id}/`);
  },
};

// TMS Service API
export const tmsServiceApi = {
  getTMSServices: async (params?: {
    isActive?: boolean;
  }): Promise<PaginatedResponse<TMSServiceApiResponse>> => {
    return apiClient.get('/spatial-data/tms-services/', params);
  },

  getTMSService: async (id: string): Promise<ApiResponse<any>> => {
    return apiClient.get(`/spatial-data/tms-services/${id}/`);
  },

  createTMSService: async (serviceData: {
    name: string;
    description?: string;
    urlTemplate?: string; // camelCase in frontend (made optional for delete operation)
    minZoom?: number;
    maxZoom?: number;
    tileSize?: number;
    attribution?: string;
    usageTerms?: string;
    extentGeojson?: any;
    isActive?: boolean;
  }): Promise<ApiResponse<any>> => {
    // Transform camelCase to snake_case for backend
    const backendData = {
      ...serviceData,
      url_template: serviceData.urlTemplate, // Convert to snake_case
    };
    if (backendData.urlTemplate !== undefined) {
      delete backendData.urlTemplate; // Remove camelCase version
    }

    return apiClient.post('/spatial-data/tms-services/', backendData);
  },

  updateTMSService: async (
    id: string,
    serviceData: Partial<any>
  ): Promise<ApiResponse<any>> => {
    return apiClient.patch(`/spatial-data/tms-services/${id}/`, serviceData);
  },

  deleteTMSService: async (id: string): Promise<void> => {
    return apiClient.delete(`/spatial-data/tms-services/${id}/`);
  },
};

export const digitalElevationApi = {
  getDigitalElevationModels: async (params?: {
    isActive?: boolean;
    tiles_generated?: boolean;
  }): Promise<PaginatedResponse<DigitalElevationModelApiResponse>> => {
    return apiClient.get('/spatial-data/dem/', params);
  },

  getDigitalElevationModel: async (
    id: string
  ): Promise<DigitalElevationModelApiResponse> => {
    return apiClient.get(`/spatial-data/dem/${id}/`);
  },

  createDigitalElevationModel: async (
    formData: FormData,
    onProgress?: (progress: number) => void,
    controller?: AbortController
  ): Promise<ApiResponse<any>> => {
    return apiClient.postFormData(
      '/spatial-data/dem/',
      formData,
      onProgress,
      controller
    );
  },

  updateDigitalElevationModel: async (
    id: string,
    demData: Partial<any>
  ): Promise<ApiResponse<any>> => {
    return apiClient.patch(`/spatial-data/dem/${id}/`, demData);
  },

  deleteDigitalElevationModel: async (id: string): Promise<void> => {
    return apiClient.delete(`/spatial-data/dem/${id}/`);
  },

  performTiling: async (id: string): Promise<ApiResponse<any>> => {
    return apiClient.post(`/spatial-data/dem/${id}/perform_tiling/`, {});
  },

  generateTextureTiles: async (id: string): Promise<ApiResponse<any>> => {
    return apiClient.post(
      `/spatial-data/dem/${id}/generate_texture_tiles/`,
      {}
    );
  },

  deleteOriginalFile: async (id: string): Promise<ApiResponse<any>> => {
    return apiClient.post(`/spatial-data/dem/${id}/delete_original_file/`, {});
  },
};

// Camera Bookmark API
export const bookmarkApi = {
  getBookmarks: async (caseId?: string): Promise<CameraBookmark[]> => {
    const params = caseId ? { violation_case: caseId } : {};
    const response = await apiClient.get<any>(
      '/spatial-data/camera-bookmarks/',
      params
    );
    // Handle paginated response from Django REST Framework
    if (response && response.results) {
      return response.results;
    }
    return response || [];
  },

  createBookmark: async (
    bookmarkData: Partial<CameraBookmark>
  ): Promise<CameraBookmark> => {
    return apiClient.post('/spatial-data/camera-bookmarks/', bookmarkData);
  },

  deleteBookmark: async (id: string): Promise<void> => {
    return apiClient.delete(`/spatial-data/camera-bookmarks/${id}/`);
  },
};

// Users API
export const usersApi = {
  getUsers: async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    role?: string;
  }): Promise<PaginatedResponse<User>> => {
    return apiClient.get('/users/', params);
  },

  createUser: async (userData: any): Promise<ApiResponse<User>> => {
    return apiClient.post('/users/', userData);
  },

  updateUser: async (id: string, userData: any): Promise<ApiResponse<User>> => {
    return apiClient.patch(`/users/${id}/`, userData);
  },

  deleteUser: async (id: string): Promise<void> => {
    return apiClient.delete(`/users/${id}/`);
  },
};

export const rolesApi = {
  getRoles: async (): Promise<PaginatedResponse<RolesResponse>> => {
    return apiClient.get('/roles/');
  },

  getAvailableRoles: async (): Promise<Role[]> => {
    return apiClient.get('/roles/available/');
  },
};

export const rbacService = {
  getPermissions: async () => {
    return apiClient.get('/permissions/');
  },

  getRolePermissions: async (roleId: RoleId): Promise<RolePermission[]> => {
    return apiClient.get(`/roles/${roleId}/permissions/`);
  },

  updateRolePermissions: async (
    roleId: RoleId,
    permissionIds: number[]
  ): Promise<void> => {
    return apiClient.patch(`/roles/${roleId}/permissions/`, {
      permission_ids: permissionIds,
    });
  },
};

// Point Cloud API (placeholder for future implementation)
export const pointCloudApi = {
  getPointClouds: async (params?: {
    isActive?: boolean;
  }): Promise<PaginatedResponse<PointCloudApiResponse>> => {
    return apiClient.get('/spatial-data/point-clouds/', params);
  },

  getPointCloud: async (id: string): Promise<ApiResponse<any>> => {
    return apiClient.get(`/spatial-data/point-clouds/${id}/`);
  },

  createPointCloud: async (
    formData: FormData,
    onProgress?: (progress: number) => void,
    controller?: AbortController
  ): Promise<ApiResponse<any>> => {
    return apiClient.postFormData(
      '/spatial-data/point-clouds/',
      formData,
      onProgress,
      controller
    );
  },

  deletePointCloud: async (id: string): Promise<void> => {
    return apiClient.delete(`/spatial-data/point-clouds/${id}/`);
  },

  // COPC conversion endpoints
  prepareToServe: async (
    id: string,
    options?: any
  ): Promise<
    ApiResponse<{
      message: string;
      task_id: string;
      estimated_time: number;
      status: string;
    }>
  > => {
    return apiClient.post(
      `/spatial-data/point-clouds/${id}/prepare_to_serve/`,
      { options }
    );
  },

  getConversionStatus: async (
    id: string
  ): Promise<ConversionStatusPointClouds> => {
    return apiClient.get(`/spatial-data/point-clouds/${id}/conversion_status/`);
  },

  cancelConversion: async (
    id: string
  ): Promise<ApiResponse<{ message: string }>> => {
    return apiClient.post(
      `/spatial-data/point-clouds/${id}/cancel_conversion/`
    );
  },

  getCOPCReady: async (): Promise<PointCloudCOPCApiResponse[]> => {
    return apiClient.get('/spatial-data/point-clouds/copc_ready/');
  },
};

// Vector Data API
export const vectorDatasetApi = {
  // Get all vector datasets
  getAll: async (): Promise<PaginatedResponse<VectorDataset>> => {
    return apiClient.get('/spatial-data/vector-datasets/');
  },

  // Upload and create new vector dataset
  upload: async (data: {
    name: string;
    description?: string;
    file: File;
  }): Promise<ApiResponse<VectorDataset>> => {
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.description) {
      formData.append('description', data.description);
    }
    formData.append('file', data.file);

    return apiClient.postFormData(
      '/spatial-data/vector-datasets/upload/',
      formData
    );
  },

  // Update vector dataset
  update: async (
    id: string,
    data: Partial<VectorDataset>
  ): Promise<ApiResponse<VectorDataset>> => {
    return apiClient.put(`/spatial-data/vector-datasets/${id}/`, data);
  },

  // Delete vector dataset
  delete: async (id: string): Promise<ApiResponse<void>> => {
    return apiClient.delete(`/spatial-data/vector-datasets/${id}/`);
  },
};

// Vector Layer API
export const vectorLayerApi = {
  // Get all vector layers
  getAll: async (
    datasetId?: string
  ): Promise<PaginatedResponse<VectorLayer>> => {
    const params = datasetId ? `?dataset=${datasetId}` : '';
    return apiClient.get(`/spatial-data/vector-layers/${params}`);
  },

  // Get vector layer by ID
  getById: async (id: number): Promise<VectorLayer> => {
    return apiClient.get(`/spatial-data/vector-layers/${id}/`);
  },

  // Get GeoJSON for a layer
  getGeoJSON: async (
    id: string,
    options?: {
      limit?: number;
      offset?: number;
      elevationMode?: ElevationMode;
    }
  ): Promise<GeoJSONFeatureCollection> => {
    const url = `/spatial-data/vector-layers/${id}/geojson/`;
    const params: Record<string, any> = {};

    if (options?.limit !== undefined) params.limit = options.limit;
    if (options?.offset !== undefined) params.offset = options.offset;
    if (options?.elevationMode !== undefined)
      params.elevation_mode = options.elevationMode;

    // Use extended timeout for GeoJSON requests as they now include elevation processing
    // which can take longer than the default 30 second timeout
    const GEOJSON_TIMEOUT = 120000; // 2 minutes for elevation processing

    return apiClient.get(url, params, { timeout: GEOJSON_TIMEOUT });
  },

  // Update vector layer
  update: async (
    id: string,
    data: Partial<VectorLayer>
  ): Promise<ApiResponse<VectorLayer>> => {
    return apiClient.patch(`/spatial-data/vector-layers/${id}/`, data);
  },

  // Update layer style
  updateStyle: async (
    id: string,
    style: Record<string, unknown>
  ): Promise<ApiResponse<VectorLayer>> => {
    return apiClient.patch(`/spatial-data/vector-layers/${id}/update_style/`, {
      style,
    });
  },

  // Delete vector layer
  delete: async (id: string): Promise<ApiResponse<void>> => {
    return apiClient.delete(`/spatial-data/vector-layers/${id}/`);
  },

  getUniqueAttributeValues: async <T extends 'string' | 'numeric'>(
    id: string,
    attribute: string,
    fieldType: T,
    rangesCount?: number
  ): Promise<T extends 'string' ? string[] : NumericRange[]> => {
    const params = new URLSearchParams({
      field_name: attribute,
      field_type: fieldType,
    });

    if (rangesCount) {
      params.append('ranges_count', rangesCount.toString());
    }

    return apiClient.get(
      `/spatial-data/vector-layers/${id}/field_values/?${params.toString()}`
    );
  },

  exportShapefile: async (
    layerId: number,
    layerName: string
  ): Promise<void> => {
    try {
      const blob = await apiClient.get<Blob>(
        `/spatial-data/layers/export/?layer_id=${layerId}`,
        {},
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${layerName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export shapefile:', error);
      throw error;
    }
  },
};

// Violation Detection API
export const violationDetectionApi = {
  // Detection Configuration APIs
  getConfigs: async (): Promise<
    PaginatedResponse<ViolationDetectionConfig>
  > => {
    return apiClient.get('/detection-configs/');
  },

  createConfig: async (config: {
    name: string;
    squatting_tolerance?: number;
    coordinate_system?: number;
    legal_plots_layer?: number | null;
    actual_plots_layer?: number | null;
    buildings_layer?: number | null;
    height_field?: string;
    plot_number_field?: string;
  }): Promise<ApiResponse<any>> => {
    return apiClient.post('/detection-configs/', config);
  },

  updateConfig: async (id: string, config: any): Promise<ApiResponse<any>> => {
    return apiClient.patch(`/detection-configs/${id}/`, config);
  },

  // Detection Run APIs
  getRuns: async (params?: {
    config?: number;
    status?: string;
  }): Promise<PaginatedResponse<any>> => {
    return apiClient.get('/detection-runs/', params);
  },

  getRun: async (
    id: number
  ): Promise<{
    id: number;
    config: number;
    config_name: string;
    started_at: string;
    completed_at: string | null;
    status: string;
    status_display: string;
    legal_plots_layer: number;
    legal_plots_layer_id: number;
    actual_plots_layer: number;
    actual_plots_layer_id: number;
    buildings_layer: number;
    buildings_layer_id: number;
    legal_plots_layer_name: string;
    actual_plots_layer_name: string;
    buildings_layer_name: string;
    height_field: string;
    plot_number_field: string;
    violations_found: number;
    error_message: string | null;
    parameters: {
      coordinate_system: number;
      max_building_height: number;
      min_building_height: number;
      squatting_tolerance: number;
      max_buildup_percentage: number;
      min_buildup_percentage: number;
    };
  }> => {
    return apiClient.get(`/detection-runs/${id}/`);
  },

  startDetection: async (data: {
    config_id: number;
    legal_plots_layer?: number | null;
    buildings_layer?: number | null;
    height_field?: string;
    actual_plots_layer?: number | null;
    plot_number_field?: string;
    // UI detection parameters
    min_building_height?: number;
    max_building_height?: number;
    min_buildup_percentage?: number;
    max_buildup_percentage?: number;
  }): Promise<ApiResponse<any>> => {
    return apiClient.post('/detection-runs/start_detection/', data);
  },

  getDetectionResults: async (
    runId: number
  ): Promise<{
    run_id: number;
    status: string;
    started_at: string;
    completed_at: string | null;
    total_violations: number;
    violations_by_type: {
      squatting: number;
      buildup: number;
      height: number;
    };
    violations_by_status: {
      detected: number;
      not_violation: number;
      confirmed: number;
      resolving: number;
      resolved: number;
    };
  }> => {
    return apiClient.get(`/detection-runs/${runId}/results/`);
  },

  deleteRun: async (id: string): Promise<void> => {
    return apiClient.delete(`/detection-runs/${id}/`);
  },
};

// Violation Layer API - for displaying violations as vector layers
export const violationLayerApi = {
  // Get available violation layers
  getAll: async (): Promise<PaginatedResponse<ViolationLayer>> => {
    return apiClient.get('/spatial-data/violation-layers/');
  },

  // Update violation layer (including style)
  update: async (
    layerId: string,
    data: Partial<{
      name: string;
      description: string;
      style: Record<string, any>;
      is_visible: boolean;
    }>
  ): Promise<ApiResponse<any>> => {
    return apiClient.patch(`/spatial-data/violation-layers/${layerId}/`, data);
  },

  // Get GeoJSON for a violation layer
  getGeoJSON: async (
    layerId: string,
    options?: {
      elevationMode?: ElevationMode;
    }
  ): Promise<GeoJSONFeatureCollection> => {
    const params: Record<string, any> = {};
    if (options?.elevationMode !== undefined)
      params.elevation_mode = options.elevationMode;

    const VIOLATION_GEOJSON_TIMEOUT = 60000; // 1 minute for violation processing
    return apiClient.get(
      `/spatial-data/violation-layers/${layerId}/geojson/`,
      params,
      { timeout: VIOLATION_GEOJSON_TIMEOUT }
    );
  },

  // Get cache status for a violation layer
  getCacheStatus: async (
    layerId: string
  ): Promise<
    ApiResponse<{
      layer_id: string;
      cache_key: string;
      status: 'pending' | 'processing' | 'completed' | 'failed';
      progress: number;
      total: number;
      error: string | null;
      started_at: number | null;
      completed_at: number | null;
    }>
  > => {
    return apiClient.get(`/spatial-data/violation-layers/${layerId}/status/`);
  },
};

// Violation Type Style API - for managing shared styles by violation type
export const violationTypeStyleApi = {
  // Update style for a specific violation type
  updateByType: async (
    violationType: number,
    data: Partial<{
      style: Record<string, any>;
    }>
  ): Promise<ApiResponse<any>> => {
    return apiClient.patch(
      `/spatial-data/violation-type-styles/${violationType}/`,
      data
    );
  },
};

// Utility functions for environment configuration
export const getApiConfig = () => ({
  baseUrl: API_BASE_URL,
  timeout: API_TIMEOUT,
  appName: APP_NAME,
  appVersion: APP_VERSION,
  debugMode: DEBUG_MODE,
});

// Debug function to log current configuration (only in debug mode)
if (DEBUG_MODE) {
  console.log('API Configuration:', getApiConfig());
}

// Export API client for direct use if needed
export { apiClient };

// Default export with all APIs
export default {
  auth: authApi,
  bookmarks: bookmarkApi,
  users: usersApi,
  tilesets: tilesetApi,
  tmsServices: tmsServiceApi,
  digitalElevation: digitalElevationApi,
  pointClouds: pointCloudApi,
  vectorDatasets: vectorDatasetApi,
  vectorLayers: vectorLayerApi,
  violationDetection: violationDetectionApi,
  violationLayers: violationLayerApi,
  violationTypeStyles: violationTypeStyleApi,
};
